
import { useEffect, useRef, useState } from "react"
import { Button } from "@components/react/pricing/ui/button"
import { motion, useInView, useAnimation } from "framer-motion"
import { VideoPlayer } from "./video-player"
import { ArrowRight } from "lucide-react"

interface AppleHeroSectionProps {
  onRequestQuote: () => void
  onStartTrial: () => void // Now used for scrolling to packages
  onFindPlan: () => void
}

export function AppleHeroSection({ onRequestQuote, onStartTrial, onFindPlan}: AppleHeroSectionProps) {
  const controls = useAnimation()
  const heroRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(heroRef, { once: true, amount: 0.3 })
  const [isHovered, setIsHovered] = useState(false)
  const [isSecondaryHovered, setIsSecondaryHovered] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)

  // Check if we're on mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768) // 768px is the md breakpoint in Tailwind
    }

    // Check on initial load
    checkIfMobile()

    // Add event listener for window resize
    window.addEventListener("resize", checkIfMobile)

    // Clean up
    return () => window.removeEventListener("resize", checkIfMobile)
  }, [])

  // Animate elements when they come into view
  useEffect(() => {
    if (isInView) {
      controls.start("visible")
    }
  }, [controls, isInView])

  // Handle video play state change
  const handleVideoPlayStateChange = (isPlaying: boolean) => {
    setIsVideoPlaying(isPlaying)
  }

  // Scroll to packages section
  const scrollToPackages = () => {
    const el = document.getElementById("popular-packages");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
  // Variants for staggered animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: [0.25, 0.1, 0.25, 1.0], // Apple's custom easing
      },
    },
  }

  return (
    <section ref={heroRef} className="py-12 md:py-16">
      <div className="container px-4 md:px-6 py-2 md:py-4">
        <motion.div
          className="flex flex-col md:flex-row gap-6 md:gap-8 items-center"
          variants={containerVariants}
          initial="hidden"
          animate={controls}
        >
          {/* Left column - Text content */}
          <motion.div className="md:w-1/2 lg:w-5/12 md:pr-8 relative" variants={containerVariants}>
            {/* Content with improved conversion-focused copy */}
            <motion.span
              className="inline-block px-4 py-1 bg-[#ef483d] bg-opacity-10 rounded-full text-[#ef483d] text-sm font-medium mb-6"
              variants={itemVariants}
            >
              Public Sector Plans & Pricing
            </motion.span>

            <motion.h1
              className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-[#0f0f11] mb-6 md:mb-8"
              variants={itemVariants}
            >
              Secure Your Organization's Data with Confidence
            </motion.h1>

            <motion.p
              className="text-lg md:text-xl mb-10 font-light "
              variants={itemVariants}
            >
              Find the right data security plans and pricing to increase trust in data, ensure compliance with mandates,
              avoid vendor lock-in, and maximize value.
            </motion.p>

            {/* Dual CTA buttons for better conversion options */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-5">
              <Button
                className="group relative overflow-hidden bg-[#3037e2] hover:bg-[#2731c8] text-white px-8 py-6 rounded-full flex items-center justify-center gap-2 transition-all duration-300 hover:scale-[1.02]"
                onClick={onRequestQuote}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <span className="relative z-10">Get Custom Quote</span>

                {/* Animated arrow with refined animation */}
                <ArrowRight
                  className="relative z-10 h-5 w-5 transition-transform duration-300"
                  style={{
                    transform: isHovered ? "translateX(4px)" : "translateX(0)",
                  }}
                />

                {/* Button hover effect with refined animation */}
                <motion.div
                  className="absolute inset-0 bg-[#2731c8]"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{
                    scale: isHovered ? 1 : 0,
                    opacity: isHovered ? 1 : 0,
                    transition: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1.0] },
                  }}
                  style={{ originX: 0, originY: 0.5 }}
                />
              </Button>
            </motion.div>
          </motion.div>

          {/* Right column - Video */}
          <motion.div className="md:w-1/2 lg:w-7/12" variants={containerVariants}>
            <div>
              {/* Video container with Apple-style material effect */}
              <motion.div
                className="video-container relative w-full aspect-[16/9] rounded-2xl overflow-hidden shadow-[0_15px_40px_-10px_rgba(0,0,0,0.1)]"
                variants={itemVariants}
              >
                <VideoPlayer
                  videoId="adTqZiGqouc"
                  title="The AvePoint Confidence Platform for the Public Sector"
                  onPlayStateChange={handleVideoPlayStateChange}
                />

                {/* Refined decorative elements */}
                <div className="absolute -bottom-6 -right-6 w-12 h-12 rounded-full bg-[#ef483d] opacity-15 blur-xl" />
                <div className="absolute -top-6 -left-6 w-12 h-12 rounded-full bg-[#3037e2] opacity-15 blur-xl" />
              </motion.div>
              {/* Minimal Apple-style video description */}
              <motion.div className="mt-4 text-center" variants={itemVariants}>
                <p className="text-sm text-[#424245] font-light tracking-wide">
                  See how the AvePoint Confidence Platform secures organization data.{" "}
                  <span className="text-[#3037e2]">2:45</span>
                </p>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}
